import React from 'react';
import { useLocation } from 'react-router-dom';
import { usePhoneScrollRestore } from '@/hooks/usePhoneScrollRestore';
import Header from './Header';
import MainNav from './MainNav';
import Footer from './Footer';
import PromoBar from './PromoBar';
import MobileBottomNav from './MobileBottomNav';
import AppShell from './AppShell';
import MobileTopBar from './MobileTopBar';

// Phone web: flows that own the bottom of the screen hide the tab bar.
const NO_TAB_BAR = [/^\/checkout/, /^\/login/, /^\/verify/];
const PHONE_WEB = typeof window !== 'undefined' && !IS_APP && window.matchMedia('(max-width: 768px)').matches;
import { IS_APP } from '@/lib/appBridge';

// Pages that should be shown WITHOUT site header/footer (full-screen editors, etc.)
const CHROMELESS_PATHS = ['/design/editor', '/pay'];

export default function SiteChrome({ children }: { children: React.ReactNode }) {
  const pathname = useLocation().pathname;
  usePhoneScrollRestore(PHONE_WEB);

  // Phones: scrolling down tucks the header's search row away, scrolling up
  // brings it back (styled by html.chrome-collapsed in esy-theme.css).
  React.useEffect(() => {
    const root = document.documentElement;
    let lastY = window.scrollY;
    // How far the header slides: everything above the search row.
    const measure = () => {
      const bar = document.querySelector<HTMLElement>('.site-header .search-bar');
      const header = document.querySelector<HTMLElement>('.site-header');
      if (!bar || !header) return;
      const d = bar.getBoundingClientRect().top - header.getBoundingClientRect().top - 6;
      root.style.setProperty('--hdr-collapse', `${Math.max(0, Math.round(d))}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    const onScroll = () => {
      if (window.scrollY > 80 && !root.style.getPropertyValue('--hdr-collapse')) measure();
      const y = window.scrollY;
      if (y < 80) root.classList.remove('chrome-collapsed');
      else if (y > lastY + 8) root.classList.add('chrome-collapsed');
      else if (y < lastY - 8) root.classList.remove('chrome-collapsed');
      if (Math.abs(y - lastY) > 8 || y < 80) lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      root.classList.remove('chrome-collapsed');
    };
  }, []);

  // Phone web: html.m-inner swaps the logo header for the compact title bar
  // on every page except home; html.m-no-tabs hides the tab bar; main gets a
  // short enter animation per route (all styled in mobile-web.css, ≤768px).
  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('m-inner', pathname !== '/');
    root.classList.toggle('m-no-tabs', NO_TAB_BAR.some((re) => re.test(pathname)));
    const main = document.querySelector<HTMLElement>('.site-main');
    if (main) { main.classList.remove('m-enter'); void main.offsetWidth; main.classList.add('m-enter'); }
  }, [pathname]);

  // Check if current route should hide site chrome
  const isChromeless = CHROMELESS_PATHS.some((path) => pathname.startsWith(path));

  if (isChromeless) {
    // Render children without any site header/footer — full-screen mode
    return <>{children}</>;
  }

  // Inside the Android app: native shell instead of website header/footer.
  if (IS_APP) return <AppShell>{children}</AppShell>;

  // Normal pages with full site chrome. On mobile, Footer is hidden via CSS
  // (native apps don't have multi-column footers) and MobileBottomNav is
  // shown. On tablet+ the classic layout is preserved.
  // .site-scale: the storefront is laid out for a 1920px screen; laptops
  // (125% Windows scaling = ~1536px) get it scaled down proportionally
  // instead of everything looking oversized (esy-theme.css).
  return (
    <div className="site-scale">
      <PromoBar />
      <Header />
      <MobileTopBar />
      <MainNav />
      <main className="site-main">{children}</main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
}
